import React, { useState } from "react";
import { Pressable, Share, Platform } from "react-native";
import Animated, { useSharedValue, useAnimatedStyle, withTiming } from "react-native-reanimated";
import { Facebook, Linkedin } from "lucide-react-native";
import { useToken } from "@gluestack-style/react";
import { Box, HStack } from "components/ui";

interface IconWrapperProps {
	children: React.ReactNode;
	variant?: "primary" | "secondary";
	title: string;
	url: string;
	message?: string; // Optional message prop
}

const IconWrapper = ({ children, variant = "secondary", title, url, message }: IconWrapperProps) => {
	const resolvedSecondary = useToken("colors", "secondary");
	const resolvedPrimary = useToken("colors", "primary");
	const resolvedPrimaryLight = useToken("colors", "primarylight");
	const colorIdle = "transparent";
	const colorHover = variant === "primary" ? resolvedPrimary : resolvedSecondary;
	const borderColorIdle = variant === "primary" ? resolvedPrimaryLight : resolvedSecondary;

	const [isHovered, setIsHovered] = useState(false);
	const hoverProgress = useSharedValue(0);

	const animatedStyle = useAnimatedStyle(() => {
		const backgroundColor = withTiming(hoverProgress.value === 1 ? colorHover : colorIdle, { duration: 150 });
		return {
			backgroundColor,
			padding: 8,
			borderColor: borderColorIdle,
			borderWidth: 2,
			borderRadius: 999,
		};
	});

	const onShare = async () => {
		try {
			const result = await Share.share({
				message: message ?? title,
				url: url,
				title: title,
			});
			if (result.action === Share.sharedAction) {
				if (result.activityType) {
					// shared with activity type of result.activityType
				} else {
					// shared
				}
			} else if (result.action === Share.dismissedAction) {
				// dismissed
			}
		} catch (error: any) {
			console.error("Error sharing:", error.message);
		}
	};

	const handleHoverIn =
		Platform.OS === "web"
			? () => {
					hoverProgress.value = 1;
					setIsHovered(true);
				}
			: undefined;

	const handleHoverOut =
		Platform.OS === "web"
			? () => {
					hoverProgress.value = 0;
					setIsHovered(false);
				}
			: undefined;

	const iconColor = isHovered ? "white" : variant === "primary" ? resolvedPrimary : resolvedSecondary;

	return (
		<Pressable onPress={onShare} onHoverIn={handleHoverIn} onHoverOut={handleHoverOut}>
			<Animated.View style={animatedStyle}>{React.cloneElement(children as React.ReactElement, { color: iconColor })}</Animated.View>
		</Pressable>
	);
};

interface SocialShareProps {
	title: string;
	url: string;
	message?: string;
}

const SocialShare = ({ title, url, message }: SocialShareProps) => {
	return (
		<HStack alignItems="center" justifyContent="center" gap={8}>
			<IconWrapper variant="primary" title={title} url={url} message={message}>
				<Facebook />
			</IconWrapper>
			<IconWrapper variant="primary" title={title} url={url} message={message}>
				<Linkedin />
			</IconWrapper>
		</HStack>
	);
};

export default SocialShare;
